import { AssignedCampaignRow, CampaignStatsResponse, campaignStatsResponseSchema, CampaignSummaryResponse, campaignSummaryResponseSchema, ListEventType, PendingEmailAction } from "@signalist/shared-types"; // prettier-ignore

import { api } from "@/redux/api";

import { RequestPaginationParams } from "@/types/request-params";

import { ListSourceRequestPaginationParams } from "@/types/list-source-request-params";

import { ListFilterRequestParams } from "@/types/list-filter-request-params";

import { AssignStatus, UpdateType } from "@/enum/list.enum";
import { CampaignStatus } from "@/enum/campaign-status.enum";

import {
	buildDownloadFilteredQueryParams,
	buildListSearchQueryParams,
} from "./list-search-params.helper";

import {
	Organization,
	People,
	PeopleResponse,
	PeopleResponseSchema,
} from "../../pages/Apollo/schema/get-people";
import { AiColumn } from "../../pages/List/schema/ai-column";
import { CampaignResponse, CampaignResponseSchema } from "../../pages/List/schema/campaign";
import {
	ColumnConfig,
	ColumnConfigResponse,
	ColumnConfigResponseSchema,
	ColumnConfigSchema,
} from "../../pages/List/schema/columnConfig";
import { ColumnValue } from "../../pages/List/schema/columnValue";
import {
	Archive,
	ArchiveResponse,
	ArchiveResponseSchema,
	BulkAddCompanyToTracker,
	BulkAddToAnotherList,
	BulkAddToAnotherListResponse,
	BulkAddToAnotherListResponseSchema,
	BulkAddToTracker,
	BulkAddToWebhook,
	BulkAssign,
	BulkAssignListView,
	BulkAssignResponse,
	BulkAssignResponseSchema,
	BulkRetriggerLeadScoring,
	BulkRetriggerLeadScoringResponse,
	BulkRetriggerLeadScoringResponseSchema,
	BulkRunPromptRequest,
	BulkRunPromptResponse,
	BulkRunPromptResponseSchema,
	BulkSearchForCompanyEmailAndPhone,
	BulkSearchForEmails,
	BulkSearchForPhones,
	CustomColumn,
	DownloadFile,
	DownloadFilteredRequestParams,
	Filter,
	FilterByValue,
	FilterByValueSchema,
	FilterSchema,
	GetUnassignedListRequestPaginationParams,
	List,
	ListAiEnrichmentResponse,
	ListAiEnrichmentResponseSchema,
	ListCreateRecommendationRequest,
	ListCreateRecommendationRequestSchema,
	ListCreateRecommendationResponse,
	ListCreateRecommendationResponseSchema,
	ListLogResponse,
	ListLogResponseSchema,
	BulkAddPersonSyncItem,
	BulkAddPersonsToSyncListResponse,
	BulkAddPersonsToSyncListResponseSchema,
	ResolveProspectFromLinkedinUrlRequest,
	ResolveProspectFromLinkedinUrlResponse,
	ResolvedLinkedinProspectSchema,
	ListPerson,
	ListPersonResponse,
	ListPersonResponseSchema,
	ListPersonSchema,
	ListReport,
	ListReportSchema,
	ListResponse,
	ListResponseSchema,
	ListSchema,
	ListSourceResponse,
	ListSourceSchema,
	OrganizationProfilesResponse,
	OrganizationProfilesResponseSchema,
	ReplyCounts,
	ReplyCountsSchema,
	ScoreCompanyCategoryInfo,
	ScoreCompanyCategoryInfoResponseSchema,
	SmartListSource,
	SmartListSourceSchema,
	UpdateList,
	UpdateListDetails,
} from "../../pages/List/schema/list";
import { MapBusinessUpdate } from "../../pages/List/schema/mapBusiness-update";
import {
	PendingEmailUpdate,
	PendingEmailUpdateSchema,
	ProspectEmailUpdate,
	ProspectPhoneUpdate,
	ProspectUpdate,
} from "../../pages/List/schema/prospect-update";
import { MapBusiness } from "../../pages/Map/schema/mapBusiness";
import { DemoList } from "../../pages/Onboarding/schema/demo";
import { GoogleNewsResponse } from "../../pages/ProfileTracker/schema/google-news.interface";
import { LiCompanyActivitiesInterface } from "../../pages/ProfileTracker/schema/li-company-activity-interface";
import { Sender } from "../../pages/List/components/CampaignOverview/components/Sender";
import { ChangePersonaCategoryResults } from "../../pages/Score/schema/score.schema";
import { GenerateCopywritingResponse } from "../../pages/Messages/components/GenerateBar/types";
import { buildLeanListsRequest, LeanListsRequestParams } from "./lean-lists.params";
import { trackerAddInvalidatedTags } from "./list-tracker-add.tags";

export type { LeanListsRequestParams };
export { useBulkChangeStatusMutation } from "./list-bulk-status.api";

export const listApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getLists: builder.query<ListResponse, RequestPaginationParams>({
			query: ({ sorting, ...params }) => ({
				url: "list",
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: ListResponseSchema,
			}),
			providesTags: ["List"],
		}),
		getListPerson: builder.query<ListPersonResponse, RequestPaginationParams>({
			query: ({ sorting, ...params }) => ({
				url: `list/listPerson`,
				params: {
					...(params && { ...params }),
					...(sorting && { ...sorting }),
				},
				responseSchema: ListPersonResponseSchema,
			}),
			providesTags: ["ListPerson"],
		}),
		getLeanLists: builder.query<ListResponse, LeanListsRequestParams>({
			query: (params) => ({
				url: "list/lean",
				params: buildLeanListsRequest(params),
				responseSchema: ListResponseSchema,
			}),
			providesTags: ["List"],
		}),
		getListForScrape: builder.query<List, RequestPaginationParams & { id: number }>({
			query: (params) => ({
				url: `list/${params.id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListSchema,
			}),
			providesTags: ["List"],
		}),
		getListAttendees: builder.query<PeopleResponse, RequestPaginationParams & { id: number }>({
			query: (params) => ({
				url: `list/${params.id}/attendees`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: PeopleResponseSchema,
			}),
			providesTags: ["People"],
		}),
		getList: builder.query<List, number>({
			query: (id: number) => ({
				url: `list/${id}`,
				params: {
					id,
				},
				responseSchema: ListSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "List", id: result?.id || arg }],
		}),
		getLeanList: builder.query<List, { id: number; assignStatus?: AssignStatus }>({
			query: ({ id, assignStatus }) => ({
				url: `list/${id}/lean`,
				params: {
					id,
					assignStatus,
				},
				responseSchema: ListSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "List", id: result?.id || arg?.id }],
		}),
		getColumnTemplatesWithAiSnippet: builder.query<ColumnConfigResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `columns/AI_SNIPPET`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ColumnConfigResponseSchema,
			}),
			providesTags: ["ListTemplates", "ColumnTemplate"],
		}),
		getColumnTemplatesWithAiSnippetOrAiColumns: builder.query<
			ColumnConfigResponse,
			RequestPaginationParams
		>({
			query: (params) => ({
				url: `columns/templates`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ColumnConfigResponseSchema,
			}),
			providesTags: ["ListTemplates", "ColumnTemplate"],
		}),
		getColumnTemplates: builder.query<ColumnConfigResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list/column/templates`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ColumnConfigResponseSchema,
			}),
			providesTags: ["ListTemplates"],
		}),
		getLog: builder.query<
			ListLogResponse,
			RequestPaginationParams & { id: number; iteration?: number }
		>({
			query: ({ id, iteration, ...params }) => ({
				url: `list/log/${id}${iteration ? `/${iteration}` : ""}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListLogResponseSchema,
			}),
			providesTags: ["ListLog"],
		}),
		downloadList: builder.query<string, { id: number; excluded?: boolean }>({
			query: ({ id, excluded }) => ({
				url: `list/download/${id}${excluded ? "?excluded=true" : ""}`,
				params: {
					id,
				},
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				responseHandler: (response: any) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		downloadSelectedList: builder.query<Blob, { files: DownloadFile[] }>({
			query: (body) => ({
				url: `list/downloadSelected`,
				method: "POST",
				body,

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
		}),
		createDemoLists: builder.mutation<{ status: string }, DemoList>({
			query: (body) => ({
				url: `list/demo`,
				method: "POST",
				body,
			}),
		}),
		getListSources: builder.query<ListSourceResponse, ListSourceRequestPaginationParams>({
			query: ({ id, ...params }) => ({
				url: `list/sources/${id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListSourceSchema,
			}),
			providesTags: ["List"],
		}),
		getAddedListSources: builder.query<ListSourceResponse, ListSourceRequestPaginationParams>({
			query: ({ id, ...params }) => ({
				url: `list/addedSources/${id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListSourceSchema,
			}),
			providesTags: ["List"],
		}),
		getFilters: builder.query<Filter, ListFilterRequestParams>({
			query: ({ id, ...params }) => ({
				url: `list/filter/${id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: FilterSchema,
			}),
		}),
		getFilterByProperty: builder.query<
			FilterByValue,
			{ propertyName: string; searchTerm?: string; listId?: number }
		>({
			query: ({ propertyName, searchTerm, listId }) => ({
				url: `list/filterByProperty/${propertyName}`,
				params: {
					searchTerm,
					listId,
				},
				responseSchema: FilterByValueSchema,
			}),
		}),
		getSimilarTitles: builder.mutation<
			string[],
			{ searchTerm: string; listId: number; decisionMakersOnly: boolean }
		>({
			query: ({ searchTerm, listId, decisionMakersOnly }) => ({
				url: `list/similarTitles`,
				method: "POST",
				body: {
					searchTerm,
					listId,
					decisionMakersOnly,
				},
			}),
		}),
		getAllProspects: builder.query<ListPersonResponse, GetUnassignedListRequestPaginationParams>({
			query: ({ specialType, listPersonIds, ...rest }) => {
				return {
					url: `list/all-prospects`,
					params: {
						...buildListSearchQueryParams(rest),
						...(listPersonIds !== undefined && {
							listPersonIds: JSON.stringify(listPersonIds),
						}),
						specialType: specialType,
					},
					responseSchema: ListPersonResponseSchema,
				};
			},
			providesTags: ["ListPerson"],
		}),
		countAllProspects: builder.query<{ count: number }, GetUnassignedListRequestPaginationParams>({
			query: (args) => ({
				url: `list/count-all-prospects`,
				params: buildListSearchQueryParams(args),
			}),
			providesTags: ["ListPerson"],
		}),
		getReplyCounts: builder.query<ReplyCounts, GetUnassignedListRequestPaginationParams>({
			query: ({
				sorting,
				filter,
				priorityFilterStatus,
				sentimentFilterStatus,
				inclusionFilterStatus,
				...params
			}) => {
				return {
					url: `list/reply-counts`,
					params: {
						...(params && { ...params }),
						...(sorting && { ...sorting }),
						...(filter && { filter: JSON.stringify(filter) }),
						...(priorityFilterStatus && {
							priorityFilterStatus: JSON.stringify(priorityFilterStatus),
						}),
						...(sentimentFilterStatus &&
							sentimentFilterStatus.length > 0 && {
								sentimentFilterStatus: JSON.stringify(sentimentFilterStatus),
							}),
						...(inclusionFilterStatus && {
							inclusionFilterStatus: JSON.stringify(inclusionFilterStatus),
						}),
					},
					responseSchema: ReplyCountsSchema,
				};
			},
			providesTags: ["ListPerson"],
		}),
		getUnassignedAndDummy: builder.query<
			ListPersonResponse,
			GetUnassignedListRequestPaginationParams
		>({
			query: (args) => ({
				url: `list/unassigned-and-dummy`,
				params: buildListSearchQueryParams(args),
			}),
			providesTags: ["ListPerson"],
		}),
		getUnassigned: builder.query<ListPersonResponse, GetUnassignedListRequestPaginationParams>({
			query: (args) => ({
				url: `list/unassigned`,
				params: buildListSearchQueryParams(args),
			}),
			providesTags: ["ListPerson"],
		}),
		downloadFiltered: builder.query<Blob, DownloadFilteredRequestParams>({
			query: (args) => ({
				url: `list/download-filtered`,
				params: buildDownloadFilteredQueryParams(args),

				responseHandler: async (response: Response) => {
					if (!response.ok) {
						return { error: response.statusText, status: response.status };
					}

					return response.blob();
				},
			}),
			providesTags: ["ListPerson"],
		}),
		getListAccountFilter: builder.query<ListSourceResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list/accountFilter`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListSourceSchema,
			}),
			providesTags: ["List"],
		}),
		getListLeadFilter: builder.query<ListSourceResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list/leadFilter`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListSourceSchema,
			}),
			providesTags: ["List"],
		}),
		getListAiEnrichment: builder.query<ListAiEnrichmentResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list/aiEnrichment`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListAiEnrichmentResponseSchema,
			}),
			providesTags: ["List"],
		}),
		getAllCampaigns: builder.query<CampaignResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list/campaigns`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: CampaignResponseSchema,
			}),
		}),
		getCampaignsForCopy: builder.query<CampaignResponse, RequestPaginationParams & { id: number }>({
			query: ({ id, ...params }) => ({
				url: `list/campaigns/${id}`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: CampaignResponseSchema,
			}),
		}),
		getColumnById: builder.query<ColumnConfig, number>({
			query: (id) => ({
				url: `list/column/${id}`,
				responseSchema: ColumnConfigSchema,
			}),
			providesTags: [{ type: "Column", id: "DETAIL" }],
		}),
		getAssignedToCampaign: builder.query<{ count: number }, { listId: number; campaignId: string }>(
			{
				query: ({ campaignId }) => ({
					url: `list/assigned-to-campaign/${campaignId}`,
					method: "GET",
				}),
				providesTags: (result, error, arg) => [{ type: "List", id: arg.listId }],
			}
		),
		getAssignedToCampaigns: builder.query<{ data: AssignedCampaignRow[] }, { listId: number }>({
			query: ({ listId }) => ({
				url: `list/assigned-to-campaigns/${listId}`,
				method: "GET",
			}),
			providesTags: (result, error, arg) => [{ type: "ListAssignedToCampaign", id: arg.listId }],
		}),
		getCampaignStats: builder.query<CampaignStatsResponse, { listId: number }>({
			query: ({ listId }) => ({
				url: `list/campaign-stats/${listId}`,
				method: "GET",
				responseSchema: campaignStatsResponseSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "ListAssignedToCampaign", id: arg.listId }],
		}),
		// Lazy per-list fetch powering the List Overview expandable campaign sub-rows. Tagged on the
		// list id so a per-campaign status toggle (updateCampaignStatus invalidates List/id) refreshes
		// the badges automatically.
		getListCampaignSummary: builder.query<CampaignSummaryResponse, { listId: number }>({
			query: ({ listId }) => ({
				url: `list/${listId}/campaign-summary`,
				method: "GET",
				responseSchema: campaignSummaryResponseSchema,
			}),
			providesTags: (result, error, arg) => [{ type: "List", id: arg.listId }],
		}),
		getSingleListPerson: builder.query<ListPerson, number>({
			query: (id) => ({
				url: `list_person/${id}`,
				method: "GET",
				responseSchema: ListPersonSchema,
			}),
			// Tagged so ListPerson-invalidating mutations (e.g. select/replace-prospect on a
			// placeholder lead) refresh the lead panel's chatLead instead of leaving it stale.
			providesTags: (result, error, id) => [{ type: "ListPerson", id }],
		}),
		// Lazy per-lead fetch powering the color-dot relationship hover (PREC-3547). Keyed/tagged on
		// the listPersonId so each lead caches independently — hovering different dots of the same
		// lead reuses one fetch.
		getAllPersons: builder.query<ListPersonResponse, RequestPaginationParams>({
			query: (params) => ({
				url: `list_person`,
				params: {
					...(params && { ...params }),
				},
				responseSchema: ListPersonSchema,
			}),
		}),
		runListNow: builder.mutation<void, number>({
			query: (id) => ({
				url: `list/run-now/${id}`,
				method: "POST",
			}),
			invalidatesTags: ["List"],
		}),
		createList: builder.mutation<List, Partial<List>>({
			query: (body) => ({
				url: `list`,
				method: "POST",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List", "WebsiteScrape"],
		}),
		recommendCreateListTypes: builder.mutation<
			ListCreateRecommendationResponse,
			ListCreateRecommendationRequest
		>({
			query: (body) => ({
				url: `list/recommend-create-types`,
				method: "POST",
				body,
				requestSchema: ListCreateRecommendationRequestSchema,
				responseSchema: ListCreateRecommendationResponseSchema,
			}),
		}),
		createSmartList: builder.mutation<
			List,
			{
				name: string;
				filter?: string;
				semanticSearchProspects?: string;
				frequency: string;
			}
		>({
			query: (body) => ({
				url: `list/smart-list`,
				method: "POST",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List"],
		}),
		addScoresToList: builder.mutation<Partial<List>, { listId: number; scoreIds: number[] }>({
			query: ({ listId, ...body }) => ({
				url: `list/addScoresToList/${listId}`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["Score"],
		}),
		updateList: builder.mutation<Partial<List>, Partial<UpdateList>>({
			query: ({ id, ...body }) => {
				return {
					url: `list/${id}`,
					method: "PATCH",
					body,
					responseSchema: ListSchema,
				};
			},
			invalidatesTags: (result, error, arg) => {
				if (arg.updateType === UpdateType.STATUS_WITH_REFETCH) {
					return ["List", "ListPerson", "ColumnTemplate", "LinkedinEnrichmentSettings"];
				}

				return [{ type: "List", id: arg.id }, "ColumnTemplate", "LinkedinEnrichmentSettings"];
			},
		}),
		updateCampaignStatus: builder.mutation<
			Partial<List>,
			{ id: number; campaignUuid: string; status: CampaignStatus }
		>({
			query: ({ id, campaignUuid, status }) => ({
				url: `list/${id}/campaign/${campaignUuid}/status`,
				method: "PATCH",
				body: { status },
				responseSchema: ListSchema,
			}),
			invalidatesTags: (result, error, arg) => [{ type: "List", id: arg.id }],
		}),
		updateListEvent: builder.mutation<
			Partial<List>,
			{
				id: number;
				enabled?: boolean;
				eventName: string;
				listType: ListEventType;
				startDate: string;
				endDate: string;
				meetingLocation?: string;
				meetingLink?: string;
				meetingTimes?: string[];
				customVariables?: { key: string; value: string }[];
			}
		>({
			query: ({ id, ...body }) => ({
				url: `list/${id}/event`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: (result, error, arg) => [{ type: "List", id: arg.id }],
		}),
		archiveList: builder.mutation<ArchiveResponse, Archive>({
			query: (body) => ({
				url: `list/archive`,
				method: "PATCH",
				body,
				responseSchema: ArchiveResponseSchema,
			}),
			invalidatesTags: ["List", "LinkedinEnrichmentSettings"],
		}),
		removeSourceFromList: builder.mutation<Partial<List>, { id: number; sourceId: number }>({
			query: ({ id, ...body }) => ({
				url: `list/removeSourceFromList/${id}`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: (result, error, arg) => [{ type: "List", id: arg.id }],
		}),
		addSmartListSource: builder.mutation<
			SmartListSource,
			{ listId: number; name: string; filter: string }
		>({
			query: ({ listId, ...body }) => ({
				url: `list/smart-list/${listId}/source`,
				method: "POST",
				body,
				responseSchema: SmartListSourceSchema,
			}),
			invalidatesTags: (result, error, arg) => (error ? [] : [{ type: "List", id: arg.listId }]),
		}),
		updateSmartListSource: builder.mutation<
			SmartListSource,
			{ listId: number; sourceId: number; name: string; filter: string }
		>({
			query: ({ listId, sourceId, ...body }) => ({
				url: `list/smart-list/${listId}/source/${sourceId}`,
				method: "PATCH",
				body,
				responseSchema: SmartListSourceSchema,
			}),
			invalidatesTags: (result, error, arg) => (error ? [] : [{ type: "List", id: arg.listId }]),
		}),
		removeSmartListSource: builder.mutation<void, { listId: number; sourceId: number }>({
			query: ({ listId, sourceId }) => ({
				url: `list/smart-list/${listId}/source/${sourceId}`,
				method: "DELETE",
			}),
			invalidatesTags: (result, error, arg) => (error ? [] : [{ type: "List", id: arg.listId }]),
		}),
		previewSmartListCount: builder.mutation<{ count: number }, { filter: string }>({
			query: (body) => ({
				url: `list/smart-list/preview-count`,
				method: "POST",
				body,
			}),
		}),
		createListTrackerSource: builder.mutation<void, FormData>({
			query: (body) => ({
				url: `list/tracker-source`,
				method: "POST",
				body,
			}),
			invalidatesTags: (result, error, arg) => {
				if (arg.get("listId")) {
					return [{ type: "List", id: +(arg.get("listId") || 0) }];
				}

				return ["List"];
			},
		}),
		updateListDetails: builder.mutation<Partial<List>, UpdateListDetails>({
			query: ({ id, ...body }) => ({
				url: `list/${id}`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: (result, error, arg) => [{ type: "List", id: arg.id }],
		}),
		updateListColumns: builder.mutation<Partial<List>, Partial<UpdateList>>({
			query: ({ id, ...body }) => ({
				url: `list/${id}`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: (result, error, arg) => [
				"ListPerson",
				"Column",
				"ColumnTemplate",
				{ type: "List", id: arg.id },
			],
		}),
		deleteListColumn: builder.mutation<number, Partial<UpdateList>>({
			query: ({ id }) => ({
				url: `list/column/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: (result) => {
				return ["ListPerson", "Column", { type: "List", id: result }];
			},
		}),
		updateCostumColumn: builder.mutation<{ id: number; columnValue: ColumnValue }, CustomColumn>({
			query: ({ id, ...body }) => ({
				url: `list/person/${id}`,
				method: "PATCH",
				body,
			}),
			invalidatesTags: ["ColumnTemplate"],
		}),
		updateProspect: builder.mutation<ProspectUpdate, { id: number } & Partial<People>>({
			query: ({ id, ...body }) => ({
				url: `list/prospect/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		updateProspectPendingEmail: builder.mutation<
			PendingEmailUpdate,
			{ id: number; pendingEmailId: number; action: PendingEmailAction }
		>({
			query: ({ id, pendingEmailId, action }) => ({
				url: `list/prospect/${id}/pending-email`,
				method: "PATCH",
				body: { pendingEmailId, action },
				responseSchema: PendingEmailUpdateSchema,
			}),
		}),
		updateListPerson: builder.mutation<ListPerson, Partial<ListPerson> & { id: number }>({
			query: ({ id, ...body }) => ({
				url: `list_person/${id}`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
		}),
		updateListPersonSenders: builder.mutation<ListPerson, { id: number; senders: Sender[] | null }>(
			{
				query: ({ id, senders }) => ({
					url: `list_person/${id}/senders`,
					method: "PATCH",
					body: { senders },
					responseSchema: ListPersonSchema,
				}),
			}
		),
		updateListPersonCampaign: builder.mutation<
			ListPerson,
			{ id: number; campaignId?: string | null; lemlistId?: number | null }
		>({
			query: ({ id, ...body }) => ({
				url: `list_person/${id}/campaign`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
		}),
		updateCopywritingOutput: builder.mutation<
			ListPerson,
			{ id: number; copywritingOutput: GenerateCopywritingResponse }
		>({
			query: ({ id, copywritingOutput }) => ({
				url: `list_person/${id}/copywriting-output`,
				method: "PATCH",
				body: { copywritingOutput },
				responseSchema: ListPersonSchema,
			}),
		}),
		updateLeadScoringResult: builder.mutation<ProspectUpdate, ChangePersonaCategoryResults>({
			query: ({ id, ...body }) => ({
				url: `list_person/scoring/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		updateSignalPriority: builder.mutation<
			{ id: number; priority: string; updatedAt: string },
			{ trackerSignalResultId: number; priority: string }
		>({
			query: ({ trackerSignalResultId, priority }) => ({
				url: `signal/tracker-signal-result/${trackerSignalResultId}/priority`,
				method: "PATCH",
				body: { priority },
			}),
		}),
		updateListPersonFromTrackerPerson: builder.mutation<
			{ id: number; prospectId: number; mode: string },
			{ listPersonId: number; trackerPersonId: number; mode: "replace" | "add" }
		>({
			query: ({ listPersonId, trackerPersonId, mode }) => ({
				url: `list_person/${listPersonId}/tracker-person`,
				method: "PATCH",
				body: { trackerPersonId, mode },
			}),
			// Only invalidate tags on success, not on error (to keep modal open)
			invalidatesTags: (result, error) => (error ? [] : ["ListPerson"]),
		}),
		searchForEmails: builder.mutation<ProspectEmailUpdate, BulkSearchForEmails>({
			query: (body) => ({
				url: `list/searchForEmails`,
				method: "PATCH",
				body,
			}),
		}),
		searchForPhones: builder.mutation<ProspectPhoneUpdate, BulkSearchForPhones>({
			query: (body) => ({
				url: `list/searchForPhones`,
				method: "PATCH",
				body,
			}),
		}),
		searchForCompanyEmailAndPhone: builder.mutation<void, BulkSearchForCompanyEmailAndPhone>({
			query: (body) => ({
				url: `list/searchForCompanyEmailAndPhone`,
				method: "PATCH",
				body,
			}),
		}),
		updateMapBusiness: builder.mutation<MapBusinessUpdate, { id: number & Partial<MapBusiness> }>({
			query: ({ id, ...body }) => ({
				url: `list/mapBusiness/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		updateOrganization: builder.mutation<ProspectUpdate, { id: number & Partial<Organization> }>({
			query: ({ id, ...body }) => ({
				url: `list/organization/${id}`,
				method: "PATCH",
				body,
			}),
		}),
		runAiEnrichment: builder.mutation<{ data: string }, AiColumn>({
			query: ({ id, ...body }) => ({
				url: `list/run/${id}`,
				method: "POST",
				body,
			}),
		}),
		bulkAddToTracker: builder.mutation<BulkAssignResponse, BulkAddToTracker>({
			query: (body) => ({
				url: `list/bulkAddToTracker`,
				method: "PATCH",
				body,
				responseSchema: BulkAssignResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => trackerAddInvalidatedTags(error, arg),
		}),
		bulkAddCompanyToTracker: builder.mutation<BulkAssignResponse, BulkAddCompanyToTracker>({
			query: (body) => ({
				url: `list/bulkAddCompanyToTracker`,
				method: "PATCH",
				body,
				responseSchema: BulkAssignResponseSchema,
			}),
			invalidatesTags: (result, error, arg) => trackerAddInvalidatedTags(error, arg),
		}),
		bulkAddToWebhook: builder.mutation<BulkAssignResponse, BulkAddToWebhook>({
			query: (body) => ({
				url: `list/bulkAddToWebhook`,
				method: "PATCH",
				body,
				responseSchema: BulkAssignResponseSchema,
			}),
			invalidatesTags: (result, error, arg) =>
				arg.webhookId ? [{ type: "Webhook", id: arg.webhookId }, "ListPerson"] : ["ListPerson"],
		}),
		bulkRetriggerLeadScoring: builder.mutation<
			BulkRetriggerLeadScoringResponse,
			BulkRetriggerLeadScoring
		>({
			query: (body) => ({
				url: `list/bulkRetriggerLeadScoring`,
				method: "POST",
				body,
				responseSchema: BulkRetriggerLeadScoringResponseSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		bulkRunPrompt: builder.mutation<BulkRunPromptResponse, BulkRunPromptRequest>({
			query: (body) => ({
				url: `list/bulkRunPrompt`,
				method: "POST",
				body,
				responseSchema: BulkRunPromptResponseSchema,
			}),
		}),
		bulkAssign: builder.mutation<BulkAssignResponse, BulkAssign>({
			query: (body) => ({
				url: `list/bulkAssign`,
				method: "PATCH",
				body,
				responseSchema: BulkAssignResponseSchema,
			}),
			invalidatesTags: (result, error, arg) =>
				arg.listId
					? [{ type: "List", id: arg.listId }, "ListPerson", "Campaign"]
					: ["ListPerson", "Campaign"],
		}),
		bulkAssignListView: builder.mutation<Partial<List>, BulkAssignListView>({
			query: (body) => ({
				url: `list/bulkAssignListView`,
				method: "PATCH",
				body,
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["Campaign"],
		}),
		bulkAddToAnotherList: builder.mutation<BulkAddToAnotherListResponse, BulkAddToAnotherList>({
			query: (body) => ({
				url: `list/bulkAddToAnotherList`,
				method: "POST",
				body,
				responseSchema: BulkAddToAnotherListResponseSchema,
			}),
			invalidatesTags: ["ListPerson", "List"],
		}),
		createSlackMessage: builder.mutation<Partial<ListReport>, ListReport>({
			query: (body) => ({
				url: `list/createSlackMessage`,
				method: "POST",
				body,
				responseSchema: ListReportSchema,
			}),
		}),
		deleteListPrompt: builder.mutation<Partial<List>, { id: number; promptId: number }>({
			query: ({ id, promptId }) => ({
				url: `list/${id}/${promptId}`,
				method: "DELETE",
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List"],
		}),
		resetList: builder.mutation<Partial<List>, number>({
			query: (id) => ({
				url: `list/reset/${id}`,
				method: "PATCH",
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List"],
		}),
		deleteList: builder.mutation<Partial<List>, number>({
			query: (id) => ({
				url: `list/${id}`,
				method: "DELETE",
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List", "LinkedinEnrichmentSettings"],
		}),
		deleteLists: builder.mutation<ArchiveResponse, number[]>({
			query: (ids) => ({
				url: `list/delete`,
				method: "POST",
				body: { ids },
				responseSchema: ArchiveResponseSchema,
			}),
			invalidatesTags: ["List", "LinkedinEnrichmentSettings"],
		}),

		runGoogleSearchForList: builder.mutation<{ data: GoogleNewsResponse }, AiColumn>({
			query: ({ id, ...body }) => ({
				url: `list-column/search/${id}`,
				method: "POST",
				body,
			}),
		}),
		runLinkedinPostSearchForList: builder.mutation<
			{ data: LiCompanyActivitiesInterface },
			AiColumn
		>({
			query: ({ id, ...body }) => ({
				url: `list-column/linkedin-post-search/${id}`,
				method: "POST",
				body,
			}),
		}),
		runAiPromptForList: builder.mutation<
			{
				data: {
					answer: string;
					sourceUrl: string;
				};
			},
			AiColumn
		>({
			query: ({ id, ...body }) => ({
				url: `list-column/run/${id}`,
				method: "POST",
				body,
			}),
		}),
		duplicateList: builder.mutation<
			Partial<List>,
			{ id: number; name: string; copyCustomColumns: boolean }
		>({
			query: ({ id, name, copyCustomColumns }) => ({
				url: `list/duplicate/${id}`,
				method: "POST",
				body: { name, copyCustomColumns },
				responseSchema: ListSchema,
			}),
			invalidatesTags: ["List"],
		}),
		bulkSnoozeProspect: builder.mutation<
			Partial<ListPerson[]>,
			{ listPersonId: number; snoozedUntil: Date }[]
		>({
			query: (body) => ({
				url: `list/bulkSnooze`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		snoozeProspect: builder.mutation<
			Partial<ListPerson>,
			{ listPersonId: number; snoozedUntil: Date }
		>({
			query: ({ listPersonId, ...body }) => ({
				url: `list_person/snooze/${listPersonId}`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		unsnoozeProspect: builder.mutation<Partial<ListPerson>, { listPersonId: number }>({
			query: ({ listPersonId }) => ({
				url: `list_person/unsnooze/${listPersonId}`,
				method: "PATCH",
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		bulkUnsnoozeProspect: builder.mutation<Partial<ListPerson[]>, { listPersonId: number }[]>({
			query: (body) => ({
				url: `list/bulkUnsnooze`,
				method: "PATCH",
				body,
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		addPersonToList: builder.mutation<
			ListPersonResponse,
			{
				method: "nameCompany" | "linkedinUrl" | "email" | "enterManually";
				linkedinUrl?: string;
				email?: string;
				enterManually?: string;
				name?: string;
				companyName?: string;
				firstName?: string;
				lastName?: string;
				enrichEmail?: boolean;
				enrichPhone?: boolean;
			}
		>({
			query: (body) => ({
				url: `v1/list/person`,
				method: "POST",
				body,
				responseSchema: ListPersonSchema,
			}),
		}),
		addPersonToSpecificList: builder.mutation<
			ListPersonResponse,
			{
				listId: number;
				method: "nameCompany" | "linkedinUrl" | "email" | "enterManually";
				linkedinUrl?: string;
				email?: string;
				enterManually?: string;
				name?: string;
				companyName?: string;
				firstName?: string;
				lastName?: string;
				enrichEmail?: boolean;
			}
		>({
			query: ({ listId, ...body }) => ({
				url: `v1/list/person/sync/${listId}`,
				method: "POST",
				body,
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		addPersonToSpecificListBulk: builder.mutation<
			BulkAddPersonsToSyncListResponse,
			{
				listId?: number | null;
				listName?: string | null;
				persons: BulkAddPersonSyncItem[];
			}
		>({
			query: ({ listId, persons, listName }) => ({
				url: listId ? `v1/list/person/sync/bulk/${listId}` : `v1/list/person/sync/bulk`,
				method: "POST",
				body: { persons, listName },
				responseSchema: BulkAddPersonsToSyncListResponseSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		resolveProspectFromLinkedinUrl: builder.mutation<
			ResolveProspectFromLinkedinUrlResponse,
			ResolveProspectFromLinkedinUrlRequest
		>({
			query: (body) => ({
				url: `v1/list/person/from-linkedin-url`,
				method: "POST",
				body,
				responseSchema: ResolvedLinkedinProspectSchema,
			}),
		}),
		getScoreCompanyCategories: builder.query<ScoreCompanyCategoryInfo[], number>({
			query: (id: number) => ({
				url: `list/${id}/score-company-categories`,
				responseSchema: ScoreCompanyCategoryInfoResponseSchema,
			}),
			providesTags: ["ScoreCompanyCategories"],
		}),
		setScoreCompanyCategories: builder.mutation<
			ScoreCompanyCategoryInfo[],
			{ id: number; scoreCompanyCategoryIds: number[] }
		>({
			query: ({ id, scoreCompanyCategoryIds }) => ({
				url: `list/${id}/score-company-categories`,
				method: "PATCH",
				body: { scoreCompanyCategoryIds },
				responseSchema: ScoreCompanyCategoryInfoResponseSchema,
			}),
		}),
		addExistingProspectToList: builder.mutation<
			ListPerson,
			{ listId: number; prospectId: number; listPersonId?: number }
		>({
			query: ({ listId, prospectId, listPersonId }) => ({
				url: `v1/list/prospect/add/${listId}`,
				method: "POST",
				body: { prospectId, ...(listPersonId ? { listPersonId } : {}) },
				responseSchema: ListPersonSchema,
			}),
			invalidatesTags: ["ListPerson"],
		}),
		getOrganizationProfiles: builder.query<OrganizationProfilesResponse, number>({
			query: (listPersonId) => ({
				url: `list_person/${listPersonId}/organization-profiles`,
				method: "GET",
				responseSchema: OrganizationProfilesResponseSchema,
			}),
			providesTags: ["ListPerson"],
		}),
	}),
});

export const {
	useGetListsQuery,
	useGetListPersonQuery,
	useLazyGetListPersonQuery,
	useLazyGetUnassignedAndDummyQuery,
	useGetLeanListsQuery,
	useLazyGetLeanListsQuery,
	useLazyGetListsQuery,
	useGetLeanListQuery,
	useLazyGetLeanListQuery,
	useGetAssignedToCampaignsQuery,
	useGetCampaignStatsQuery,
	useLazyGetListCampaignSummaryQuery,
	useGetLogQuery,
	useLazyGetLogQuery,
	useGetListQuery,
	useLazyGetListQuery,
	useGetListForScrapeQuery,
	useLazyGetListForScrapeQuery,
	useGetListAttendeesQuery,
	useGetListSourcesQuery,
	useGetListAccountFilterQuery,
	useGetListLeadFilterQuery,
	useLazyGetListLeadFilterQuery,
	useGetListAiEnrichmentQuery,
	useGetUnassignedQuery,
	useLazyGetUnassignedQuery,
	useLazyDownloadListQuery,
	useLazyDownloadSelectedListQuery,
	useGetCampaignsForCopyQuery,
	useArchiveListMutation,
	useDuplicateListMutation,
	useUpdateListMutation,
	useUpdateCampaignStatusMutation,
	useUpdateListEventMutation,
	useDeleteListMutation,
	useDeleteListsMutation,
	useDeleteListPromptMutation,
	useRunListNowMutation,
	useResetListMutation,
	useBulkAssignMutation,
	useCreateSlackMessageMutation,
	useCreateListMutation,
	useRecommendCreateListTypesMutation,
	useCreateSmartListMutation,
	useUpdateCostumColumnMutation,
	useUpdateListColumnsMutation,
	useGetFiltersQuery,
	useGetColumnByIdQuery,
	useDeleteListColumnMutation,
	useRunAiEnrichmentMutation,
	useUpdateProspectMutation,
	useUpdateProspectPendingEmailMutation,
	useUpdateOrganizationMutation,
	useUpdateListDetailsMutation,
	useGetAddedListSourcesQuery,
	useGetColumnTemplatesQuery,
	useGetColumnTemplatesWithAiSnippetQuery,
	useLazyGetFilterByPropertyQuery,
	useUpdateMapBusinessMutation,
	useAddScoresToListMutation,
	useRemoveSourceFromListMutation,
	useAddSmartListSourceMutation,
	useUpdateSmartListSourceMutation,
	useRemoveSmartListSourceMutation,
	usePreviewSmartListCountMutation,
	useCreateListTrackerSourceMutation,
	useBulkAssignListViewMutation,
	useSearchForEmailsMutation,
	useLazyDownloadFilteredQuery,
	useRunGoogleSearchForListMutation,
	useRunLinkedinPostSearchForListMutation,
	useRunAiPromptForListMutation,
	useCreateDemoListsMutation,
	useGetSimilarTitlesMutation,
	useBulkAddToTrackerMutation,
	useBulkAddCompanyToTrackerMutation,
	useBulkAddToWebhookMutation,
	useBulkRetriggerLeadScoringMutation,
	useBulkRunPromptMutation,
	useUpdateLeadScoringResultMutation,
	useUpdateSignalPriorityMutation,
	useUpdateListPersonFromTrackerPersonMutation,
	useGetAssignedToCampaignQuery,
	useLazyGetCampaignsForCopyQuery,
	useGetAllProspectsQuery,
	useLazyGetAllProspectsQuery,
	useUpdateListPersonMutation,
	useCountAllProspectsQuery,
	useLazyCountAllProspectsQuery,
	useGetReplyCountsQuery,
	useSnoozeProspectMutation,
	useUnsnoozeProspectMutation,
	useBulkSnoozeProspectMutation,
	useBulkUnsnoozeProspectMutation,
	useGetSingleListPersonQuery,
	useLazyGetSingleListPersonQuery,
	useAddPersonToListMutation,
	useGetAllPersonsQuery,
	useLazyGetAllPersonsQuery,
	useAddPersonToSpecificListMutation,
	useGetAllCampaignsQuery,
	useLazyGetAllCampaignsQuery,
	useSearchForPhonesMutation,
	useSearchForCompanyEmailAndPhoneMutation,
	useGetColumnTemplatesWithAiSnippetOrAiColumnsQuery,
	useUpdateListPersonSendersMutation,
	useUpdateListPersonCampaignMutation,
	useUpdateCopywritingOutputMutation,
	useGetScoreCompanyCategoriesQuery,
	useSetScoreCompanyCategoriesMutation,
	useBulkAddToAnotherListMutation,
	useGetOrganizationProfilesQuery,
	useLazyGetOrganizationProfilesQuery,
	useAddExistingProspectToListMutation,
	useAddPersonToSpecificListBulkMutation,
	useResolveProspectFromLinkedinUrlMutation,
} = listApi;
export const accountApiReducer = listApi.reducer;
export const accountApiMiddleware = listApi.middleware;
